import { FC, type JSX,MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenIcon from '@mui/icons-material/OpenInFull';
import { Box, Chip, Tooltip, tooltipClasses,TooltipProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import { styled, useTheme } from '@mui/material/styles';
import {
    GridActionsCellItem,
    GridActionsColDef,
    GridAlignment,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridRowModel,
    GridRowParams,
    GridRowsProp
} from '@mui/x-data-grid';
import Colors from 'assets/tokens/colors.json';
import { CopyToClipboard } from 'common/components/CopyToClipboard';
import { EntitiesStatus } from 'common/types/Entities';
import { ActionDefinition, ColumnDefinition,Operations } from 'common/types/TypeDefs';

import { dateToFrontEndFormat } from './dateHelpers';
import { getStatusDisplayName } from './statusHelpers';

export const CellWithTooltip: FC<{
    value?: string;
    copy?: boolean;
    field?: string;
}> = ({ value, copy, field }) => {
    if (field === 'startDate' || field === 'endDate') {
        value = dateToFrontEndFormat(value);
    }

    const preventClickPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {
                copy && value && <CopyToClipboard content={value}/>
            }
            {
                value && <Tooltip title={value}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -20],
                                    },
                                },
                            ],
                        },
                    }}
                    PopperProps={{
                        onClick: preventClickPropagation
                    }}>
                    <span data-testid='cell-with-tooltip' className="table-cell-truncate">{value}</span>
                </Tooltip>
            }
        </div>
    );
};

export const _getStatusChipColor = (cellParamsValue: unknown, theme: Theme) => {
    const isDark = theme.palette.mode === 'dark';
    if (isDark) {
        switch(cellParamsValue) {
            case EntitiesStatus.ACTIVE:
                return Colors.pds['dark-status-active'].value;
            case EntitiesStatus.INACTIVE:
                return Colors.pds['dark-status-inactive'].value;
            case EntitiesStatus.NOACCESS:
                return Colors.pds['dark-status-noaccess'].value;
            default:
                return undefined;
        }
    }
    switch(cellParamsValue) {
        case EntitiesStatus.ACTIVE:
            return theme.palette.success.status;
        case EntitiesStatus.INACTIVE:
            return theme.palette.info.dark;
        case EntitiesStatus.NOACCESS:
            return theme.palette.warning.light;
        default:
            return undefined;
    }
};

const CellWithChip: FC<{
    params: GridRenderCellParams;
    toolTip?: (row?: GridRowModel) => JSX.Element;
}> = ({ params, toolTip }) => {
    const theme = useTheme();
    const chipLabel = getStatusDisplayName(params.value);
    const chipColor = _getStatusChipColor(params.value, theme);
    const chip = <Chip
        data-testid='cell-with-chip'
        className="table-cell-chip"
        label={chipLabel}
        sx={{ backgroundColor: chipColor }} />;
    if(!toolTip) {
        return chip;
    }
    return <Tooltip title={toolTip(params.row)}>
        {chip}
    </Tooltip>;
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}/>
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 220,
        padding: '10px',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: theme.typography.pxToRem(12),
        boxShadow: theme.palette.mode === 'dark'
            ? '0px 2px 10px rgba(0,0,0,0.5)'
            : `0px 2px 10px ${Colors.pds['gray-100'].value}33`,
        border: `1px solid ${theme.palette.info.dark}`,
        borderRadius: '4px',
    },
}));

const prepareBubblesToRender = (bubbles?: string[]) => {
    const updatedBubbles = [];
    if(bubbles && bubbles.length > 1)
    {
        updatedBubbles.push(bubbles[0],`+${bubbles.length - 1}`);
    }
    else if(bubbles?.length === 1)
    {
        updatedBubbles.push(bubbles[0]);
    }
    return updatedBubbles.join(', ');
};

export const getFirstColumn = (
    {
        columnId,
        columnName,
        sortable = true,
        copy
    }: ColumnDefinition,
    filterIsApplied: boolean,
    filteredRows: GridRowsProp,
    rowCount?: number
): GridColDef => (
    {
        field: columnId,
        headerName: columnName,
        flex: 1,
        align: 'left' as GridAlignment,
        sortable,
        renderCell: (params: GridRenderCellParams) => (
            <CellWithTooltip
                value={params.row[columnId]}
                copy={copy === undefined ? true : !!copy}
            />
        ),
        renderHeader: () => (
            <Box sx={{ fontWeight: 500 }}>
                {columnName}
                <Chip
                    sx={filterIsApplied
                        ? { ml: 1, backgroundColor: 'primary.accent', color: 'common.white' }
                        : { ml: 1 }}
                    label={rowCount ?? filteredRows.length}
                    size="small"
                    id='rows-count'
                />
            </Box>
        ),
    }
);

function createTooltipColumn(
    config: ColumnDefinition,
    options?: { sortComparator?: (v1: string | null | undefined, v2: string | null | undefined) => number }
): GridColDef {
    const { columnId, columnName, copy, sortable = true, size, align, headerPrompt } = config;
    return {
        field: columnId,
        headerName: columnName,
        headerAlign: align ?? 'left',
        align: align ?? 'left',
        flex: size ?? 1,
        sortable,
        ...(options?.sortComparator ? { sortComparator: options.sortComparator } : {}),
        renderCell: (params) => (
            <CellWithTooltip
                value={params.value}
                copy={!!copy}
                field={columnId}
            />
        ),
        renderHeader: () => (
            <Box sx={{
                fontWeight: 500,
                verticalAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                <span>{columnName}</span>
                {headerPrompt && (
                    <Tooltip sx={{ marginLeft: '4px', fontSize: 'large' }} title={headerPrompt}>
                        <InfoOutlinedIcon />
                    </Tooltip>
                )}
            </Box>
        ),
    };
}

export const getTooltipColumn = (config: ColumnDefinition) =>
    createTooltipColumn(config);

export const getDateTimeTooltipColumn = (config: ColumnDefinition) =>
    createTooltipColumn(config, {
        sortComparator: (dateStringOne, dateStringTwo) => {
            const dateOne = dateStringOne ? new Date(dateStringOne).getTime() : 0;
            const dateTwo = dateStringTwo ? new Date(dateStringTwo).getTime() : 0;
            return dateOne - dateTwo;
        }
    });

export const getActionColumn = (
    { headerSvg, size }: ColumnDefinition,
    actionDefinitions: ActionDefinition[],
    actionCallback: (id: GridRowId, row: GridRowModel, action: ActionDefinition) => MouseEventHandler<HTMLLIElement> | undefined,
): GridActionsColDef => {
    return {
        field: 'actions',
        type: 'actions',
        headerAlign: 'right',
        align: 'right',
        flex: size ?? 0.35,
        sortable: false,
        renderHeader: () => (
            headerSvg ?
                <HtmlTooltip style={{ padding: 0 }} placement='left' title={
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'inherit',
                            border: 'none',
                            background: 'transparent',
                            fontWeight: '600',
                        }}
                    >
                        <DeleteIcon/>
                        <span>Delete all</span>
                    </button>
                }>
                    <IconButton>
                        <MoreVertIcon/>
                    </IconButton>
                </HtmlTooltip>
                : <span style={{ 'paddingRight': '10px' }}>Actions</span>
        ),
        getActions: (params: GridRowParams) =>
            actionDefinitions?.map(actionDefinition => {
                const iconSwitcher = (action: Operations) => {
                    switch (action) {
                        case Operations.Open:
                            return <OpenIcon/>;
                        case Operations.OpenLicense:
                            return <OpenIcon/>;
                        case Operations.OpenUserGroup:
                            return <OpenIcon/>;
                        case Operations.Edit:
                            return <EditIcon/>;
                        case Operations.Delete:
                            return <DeleteIcon/>;
                        default:
                            return undefined;
                    }
                };
                const disabled = typeof actionDefinition.disabled === 'function' ? actionDefinition.disabled(params.row) : actionDefinition.disabled;
                return  <GridActionsCellItem
                    key={actionDefinition.action}
                    icon={iconSwitcher(actionDefinition.action)}
                    onClick={actionCallback(params.id, params.row, actionDefinition)}
                    disabled={disabled}
                    label={actionDefinition.action}
                    component={actionDefinition.urlToOpen ? Link : 'li' }
                    {...(actionDefinition.urlToOpen ? { to: actionDefinition.urlToOpen(params.id, params.row) } : {})}
                    showInMenu/>;

            }) || []
    };
};

export const getChipColumn = ({ columnId, columnName, size, sortable = true, toolTip }: ColumnDefinition): GridColDef => (
    {
        field: columnId,
        headerName: columnName,
        flex: 1,
        minWidth: size,
        maxWidth: size,
        sortable,
        renderCell: (params) => <CellWithChip params={params} toolTip={toolTip}/>
    }
);

export const getChipsColumn = ({ columnId, columnName, size, sortable = true, withCountBubble }: ColumnDefinition): GridColDef => {
    return (
        {
            field: columnId,
            headerName: columnName,
            flex: 1,
            minWidth: size,
            maxWidth: size,
            sortable,
            renderCell: (params: { value?: string }) =>
            {   const bubblesToRender = withCountBubble ? prepareBubblesToRender(params.value?.split(', ')) : params.value;
                return (bubblesToRender?.split(', ').map(
                    value => value ? (
                        <Tooltip key={value} title={params.value ?? ''}>
                            <Chip data-testid='cell-with-chip' className="table-cell-chip" label={value} style={{ 'marginRight': '5px' }} />
                        </Tooltip>
                    ) : null
                ));
            }
        }
    );
};
